import { useState, FormEvent, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";

import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import {
  Container,
  Dialog,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";

import conf from "../../conf";
import Spinner from "../utils/Spinner";
import InfoChip from "../utils/InfoChip";
import SectionHeader from "../utils/SectionHeader";
import { useProxyProjectCreate } from "../../hooks/proxy/projects";

const CreateView = (props: any) => {
  const { open, onClose } = props;

  const [locked, setLocked] = useState(false);

  const [description, setDescription] = useState<string>("");
  const [projectName, setProjectName] = useState<string>("");
  const [projectAcronym, setProjectAcronym] = useState<string>("");

  const create = useProxyProjectCreate();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    create.mutate({ projectName, projectAcronym, description });
  };

  const resetForm = () => {
    setLocked(false);
    setDescription("");
    setProjectName("");
    setProjectAcronym("");
  };

  const endAdornment = (
    <InputAdornment position="end">
      <InfoChip
        size="medium"
        info="This attribute is used to prefix all associated keys."
      />
    </InputAdornment>
  );

  useEffect(() => {
    if (create.isSuccess) {
      resetForm();
      onClose();
    }
  }, [create.isSuccess]);

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <Container component="main" sx={conf.STYLE.DIALOG}>
        <SectionHeader
          key="project-create-header"
          title="Create: New project"
          icon={<CreateNewFolderIcon />}
          buttons={[
            <Button
              key={"project-create-reset"}
              color="inherit"
              size="small"
              disabled={locked}
              onClick={resetForm}
            >
              Reset
            </Button>,

            <Button
              key={"project-create-close"}
              size="small"
              color="inherit"
              onClick={onClose}
            >
              <CloseIcon />
            </Button>,
          ]}
        />

        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="project-name"
                name="project-name"
                label="Name"
                disabled={locked}
                value={projectName}
                onChange={(e: any) => setProjectName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="project-acronym"
                name="project-acronym"
                label="Acronym"
                disabled={locked}
                value={projectAcronym}
                onChange={(e: any) => setProjectAcronym(e.target.value)}
                InputProps={{ endAdornment }}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              id="project-description"
              name="project-description"
              label="Description"
              disabled={locked}
              value={description}
              onChange={(e: any) => setDescription(e.target.value)}
            />
          </Grid>

          <Stack direction="row" justifyContent="flex-end" alignItems="center">
            <Button
              variant={locked ? "outlined" : "contained"}
              onClick={() => setLocked(!locked)}
              sx={{ mt: 3, mb: 2, mr: 2 }}
            >
              {locked ? "Cancel" : "Save"}
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={!locked}
              sx={{ mt: 3, mb: 2 }}
            >
              {create.isPending ? <Spinner sx={{ mx: 2.5 }} /> : "Create"}
            </Button>
          </Stack>
        </Box>
      </Container>
    </Dialog>
  );
};

const Create = ({ disabled = false }: { disabled?: boolean }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <div>
      <Button
        color="secondary"
        variant="contained"
        disabled={disabled}
        onClick={handleOpen}
        startIcon={<CreateNewFolderIcon />}
      >
        Create
      </Button>

      <CreateView open={open} onClose={handleClose} />
    </div>
  );
};

export default Create;
